/**
 * components/object/StationChip.jsx — Iter 50 reusable STA / Offset chip.
 *
 * Drop-in inline pill that:
 *   1. Calls `/api/sheets/{id}/stations/resolve` lazily on mount
 *   2. Renders "STA 123+50 · RT 12.5'" in muted mono — operational, calm
 *   3. Renders nothing (null) when the sheet has no alignment, so it
 *      stays invisible on plan sets that don't carry stationing yet
 *
 * Used by: GpsPanel readout, StructureInspector summary strip,
 *          MeasurementsLayer label augmentation.
 */
import React, { useEffect, useRef, useState } from "react";
import { Navigation2 } from "lucide-react";
import { resolveStationOnSheet, formatStationAndOffset } from "@/lib/stationing";

export default function StationChip({
  sheetId, nx, ny, alignmentId,
  compact = false, className = "", testId = "station-chip",
}) {
  const [state, setState] = useState({ loading: false, value: null });
  const lastKey = useRef("");

  useEffect(() => {
    if (!sheetId || nx == null || ny == null) {
      setState({ loading: false, value: null });
      return;
    }
    const key = `${sheetId}:${nx.toFixed(4)}:${ny.toFixed(4)}:${alignmentId || ""}`;
    if (key === lastKey.current) return;
    lastKey.current = key;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    resolveStationOnSheet(sheetId, nx, ny, { alignmentId }).then((r) => {
      if (cancelled) return;
      setState({ loading: false, value: r });
    });
    return () => { cancelled = true; };
  }, [sheetId, nx, ny, alignmentId]);

  const txt = formatStationAndOffset(state.value);
  if (!txt) return null;

  const base = compact
    ? "inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)]"
    : "inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-[var(--fop-border)] bg-[var(--fop-surface-2)] text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)]";

  return (
    <span
      className={`${base} ${className}`}
      data-testid={testId}
      data-alignment-id={state.value?.alignment_id || ""}
      data-station={Number.isFinite(state.value?.station) ? state.value.station.toFixed(2) : ""}
    >
      <Navigation2 size={9} className="text-[var(--fop-primary)] shrink-0" />
      <span className="truncate">{txt}</span>
    </span>
  );
}
